/**
 * Single earn table — coins and XP are 1:1.
 * Shipped amounts are the fallback. Admin overrides live in getEarnCoins().
 * Idempotency stays on mCoinAwards / explorePointAwards (not this module).
 */

export const EARN_COIN_MAX = 500;

/** Firestore collection — wire value unchanged. */
export const EARN_CATALOG_COLLECTION = 'appConfig';
/** Firestore document id — wire value unchanged. */
export const EARN_CATALOG_DOC_ID = 'earnCatalog';

export const EARN_COINS = Object.freeze({
  DROP_CREATE: 20,
  DROP_PICKUP: 5,
  PLACE_SAVE: 5,
  EVENT_SAVE: 2,
  USERNAME: 10,
  FRIEND: 5,
  PHOTO: 5,
  BIO: 5,
  FIRST_MESSAGE: 2,
});

const EARN_ACTION_DEFS = Object.freeze([
  { id: 'drop-create', key: 'DROP_CREATE', label: 'Leave a drop' },
  { id: 'drop-pickup', key: 'DROP_PICKUP', label: 'Collect a drop' },
  { id: 'place-save', key: 'PLACE_SAVE', label: 'Save a place' },
  { id: 'event-save', key: 'EVENT_SAVE', label: 'Save an event' },
  { id: 'friend', key: 'FRIEND', label: 'Add a friend' },
  { id: 'first-message', key: 'FIRST_MESSAGE', label: 'First message to a friend' },
  { id: 'username', key: 'USERNAME', label: 'Set a username' },
  { id: 'photo', key: 'PHOTO', label: 'Add a photo' },
  { id: 'bio', key: 'BIO', label: 'Write a bio' },
]);

/** First bio must be at least this long (trimmed) so a one-letter bio does not pay. */
export const BIO_MIN_LENGTH = 12;

let activeEarnCoins = { ...EARN_COINS };
const earnCoinListeners = new Set();

function emitEarnCoins() {
  earnCoinListeners.forEach((listener) => listener(activeEarnCoins));
}

/** Shipped defaults, with any valid admin overrides applied. */
export function getEarnCoins() {
  return activeEarnCoins;
}

export function subscribeEarnCoins(listener) {
  earnCoinListeners.add(listener);
  return () => earnCoinListeners.delete(listener);
}

/** Whole number from 0 to EARN_COIN_MAX, or null. */
export function parseEarnAmount(raw) {
  if (typeof raw === 'number') {
    return Number.isInteger(raw) && raw >= 0 && raw <= EARN_COIN_MAX ? raw : null;
  }
  const text = String(raw ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const n = Number(text);
  if (!Number.isInteger(n) || n > EARN_COIN_MAX) return null;
  return n;
}

/** Keep shipped amounts for missing or invalid keys. Strings are ignored. */
export function mergeEarnCoins(partial) {
  const next = { ...EARN_COINS };
  if (!partial || typeof partial !== 'object') return next;
  Object.keys(EARN_COINS).forEach((key) => {
    if (typeof partial[key] !== 'number') return;
    const amount = parseEarnAmount(partial[key]);
    if (amount != null) next[key] = amount;
  });
  return next;
}

export function setActiveEarnCoins(partial) {
  activeEarnCoins = mergeEarnCoins(partial);
  emitEarnCoins();
  return activeEarnCoins;
}

export function resetActiveEarnCoins() {
  activeEarnCoins = { ...EARN_COINS };
  emitEarnCoins();
  return activeEarnCoins;
}

export function validateEarnCoins(input) {
  const message = `Enter a whole number from 0 to ${EARN_COIN_MAX} for each action.`;
  if (!input || typeof input !== 'object') {
    return { ok: false, message, coins: null };
  }
  const coins = {};
  const keys = Object.keys(EARN_COINS);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const amount = parseEarnAmount(input[key]);
    if (amount == null) return { ok: false, message, coins: null };
    coins[key] = amount;
  }
  return { ok: true, message: '', coins };
}

export function earnActions(coins = getEarnCoins()) {
  return EARN_ACTION_DEFS.map((row) => ({
    id: row.id,
    key: row.key,
    label: row.label,
    coins: coins[row.key],
  }));
}

/** Shipped rows. Live amounts come from earnActions(). */
export const EARN_ACTIONS = Object.freeze(earnActions(EARN_COINS));

export const CREATE_TOO_CLOSE_MESSAGE = 'Too close to your last drop — 0 coins';

/** Legacy `true` and a stored payout (including 0) both count as already awarded. */
export function isAwardRecorded(value) {
  return value === true || (typeof value === 'number' && Number.isFinite(value));
}

export function awardMapHas(map, key) {
  if (!map || typeof map !== 'object') return false;
  const id = String(key ?? '');
  if (!id || !Object.prototype.hasOwnProperty.call(map, id)) return false;
  return isAwardRecorded(map[id]);
}
